import { Link } from 'react-router-dom';
import {
  ArrowRight, BookOpen, Building2, CalendarCheck, CalendarDays, GraduationCap, LayoutDashboard,
  MessageSquare, Smartphone, Sparkles, Users, Wallet,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { blurbForPath, labelForPath, lmsPath } from '../content/productPages';

const icons = {
  '/school-management-system': LayoutDashboard,
  '/student-information-system': Users,
  '/school-fee-management-software': Wallet,
  '/school-attendance-system': CalendarCheck,
  '/ai-school-management': Sparkles,
  '/parent-communication': MessageSquare,
  '/academic-management': GraduationCap,
  '/multi-campus-management': Building2,
  '/download-app': Smartphone,
  [lmsPath]: BookOpen,
};

// A card-style link: icon, title, one-line description and an arrow that
// nudges on hover. `primary` is the filled call-to-action version.
export default function LinkCard({ to, title, description, icon = CalendarDays, primary = false, className }) {
  const Icon = icon;
  return (
    <Link
      to={to}
      className={cn(
        'group relative flex items-start gap-4 rounded-2xl border p-5 transition-all duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
        primary
          ? 'border-transparent bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30'
          : 'border-slate-200 bg-white shadow-sm hover:border-blue-300 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800/60 dark:hover:border-blue-500/60',
        className,
      )}
    >
      <span
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
          primary ? 'bg-white/15 text-white' : 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400',
        )}
        aria-hidden="true"
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn('block font-semibold leading-snug', primary ? 'text-white' : 'text-slate-900 dark:text-white')}>{title}</span>
        {description && <span className={cn('mt-1 block text-sm leading-relaxed', primary ? 'text-blue-100' : 'text-slate-600 dark:text-slate-400')}>{description}</span>}
      </span>
      <ArrowRight
        className={cn('mt-0.5 h-5 w-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1', primary ? 'text-white' : 'text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400')}
        aria-hidden="true"
      />
    </Link>
  );
}

// Card for one of the product landing pages, labelled from the page content.
export function ProductLinkCard({ path, ...props }) {
  return <LinkCard to={path} title={labelForPath(path)} description={blurbForPath(path)} icon={icons[path]} {...props} />;
}
